import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  SuccessToast,
  deleteMenuAiLibraryImage,
  getMenuAiLibraryImages,
  queryKeys,
  reportError,
} from '@vgrubs/components';
import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MenuAiLibraryImageResponse } from '../../../types/api/menuAi.types';

const PRELOAD_ROOT_MARGIN = '1000px';
const PAGE_SIZE = 24;

const fetchLibraryImages = async ({
  pageParam = 1,
  query = '',
}: {
  pageParam?: number;
  query?: string;
}) => {
  const { data } = await getMenuAiLibraryImages({
    page: pageParam,
    limit: PAGE_SIZE,
    // Global scope: search is the only filter (no location/brand).
    search: query || undefined,
  });

  const images = data?.data ?? [];
  const nextPage = images.length === PAGE_SIZE ? pageParam + 1 : undefined;

  return { images, nextPage };
};

/**
 * Infinite, searchable list for the AI image-library picker. Mirrors
 * `useImportPickerLocations` (useInfiniteQuery + IntersectionObserver sentinel +
 * lodash-debounced search). The search is seeded from `initialSearch` (the
 * item's name) every time the modal opens, so it lands prefiltered to the item.
 */
export const useMenuAiImageLibrary = ({
  enabled = true,
  initialSearch = '',
}: { enabled?: boolean; initialSearch?: string } = {}) => {
  const [inputValue, setInputValue] = useState(initialSearch);
  const [query, setQuery] = useState(initialSearch);
  const observerRef = useRef<HTMLDivElement>(null);

  const debouncedSearch = useMemo(() => debounce(setQuery, 500), []);

  // Re-seed the search each time the picker opens (the item name may have
  // changed since mount, and reopening should reset any prior typed search).
  useEffect(() => {
    if (enabled) {
      setInputValue(initialSearch);
      setQuery(initialSearch);
      debouncedSearch.cancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, initialSearch]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    status,
    isLoading,
  } = useInfiniteQuery({
    queryKey: queryKeys.admin.menuAiImages.list(query),
    queryFn: ({ pageParam = 1 }) => fetchLibraryImages({ pageParam, query }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000,
    // Keep the previous query's images on screen while a new search loads, so
    // the modal shows an overlay spinner over the old results instead of
    // flashing empty. `isFetching` (below) drives that overlay.
    placeholderData: keepPreviousData,
    enabled,
  });

  useEffect(() => {
    if (!enabled) return;
    const current = observerRef.current;
    if (!current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        // `!isFetching` also blocks pagination while a search refetch is in
        // flight (the previous data can still report hasNextPage).
        if (
          entry?.isIntersecting &&
          hasNextPage &&
          !isFetchingNextPage &&
          !isFetching
        ) {
          fetchNextPage();
        }
      },
      {
        threshold: 0,
        rootMargin: `0px 0px ${PRELOAD_ROOT_MARGIN} 0px`,
      }
    );

    observer.observe(current);

    return () => observer.disconnect();
  }, [enabled, hasNextPage, fetchNextPage, isFetchingNextPage, isFetching]);

  // Admin-only delete. Refetching the whole (possibly many-page) infinite list
  // would be jarring, so the deleted row is spliced out of the cached pages and
  // the other search keys are just marked stale for their next open.
  const queryClient = useQueryClient();
  const {
    mutate: deleteImage,
    variables: pendingDeleteId,
    isPending: isDeleting,
  } = useMutation({
    mutationFn: (imageId: string) => deleteMenuAiLibraryImage(imageId),
    onSuccess: (_res, imageId) => {
      queryClient.setQueryData(
        queryKeys.admin.menuAiImages.list(query),
        (prev?: {
          pages: { images: MenuAiLibraryImageResponse[]; nextPage?: number }[];
          pageParams: unknown[];
        }) =>
          prev && {
            ...prev,
            pages: prev.pages.map((page) => ({
              ...page,
              images: page.images.filter((img) => img.id !== imageId),
            })),
          }
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.admin.menuAiImages.all,
        refetchType: 'none',
      });
      SuccessToast('Image deleted.');
    },
    onError: (error) => reportError(error),
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    debouncedSearch(e.target.value);
  };

  const resetSearch = () => {
    setInputValue('');
    setQuery('');
    debouncedSearch.cancel();
  };

  const images: MenuAiLibraryImageResponse[] =
    data?.pages.flatMap((page) => page.images) ?? [];

  return {
    images,
    inputValue,
    handleInputChange,
    resetSearch,
    observerRef,
    isFetchingNextPage,
    isFetching,
    hasNextPage,
    isLoading,
    status,
    query,
    deleteImage,
    deletingId: isDeleting ? pendingDeleteId : undefined,
  };
};
